import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { useUser } from "@/providers/user-provider";
import { RewardsInfoDialog } from "@/components/ui/dialog/rewards-info-dialog";
import { useState } from "react";
import { useIdeaBoardStatus } from "@/hooks/use-idea-board-status";
import { LightbulbIcon } from "@/icons/lightbulb-icon";
import { MAX_DAILY_REWARDED_DUELS } from "types/duels/types";

export const HomeHeader = () => {
  const { setViewedPlayerId, setCurrentRoute, currentRoute } = useSPAContext();
  const { playerCountry, username, pokeCoins, pokeShards, rewardedDuelsToday } =
    useUser();
  const [isRewardsDialogOpen, setIsRewardsDialogOpen] = useState(false);
  const { rewardAvailable, rewardClaimed, votesToday } = useIdeaBoardStatus(
    !currentRoute,
  );

  const matchesRemaining = Math.max(
    0,
    MAX_DAILY_REWARDED_DUELS - (rewardedDuelsToday || 0),
  );
  const showRewardEnticement = !rewardClaimed;

  return (
    <header className="w-full flex flex-col gap-2 p-1 xs:p-2 bg-gradient-to-b from-black/90 via-black/50 to-transparent z-100 relative">
      {/* User Info & Currency Row */}
      <div className="flex items-center justify-between w-full">
        {/* User Info */}
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity active:scale-95"
          onClick={() => {
            setViewedPlayerId(undefined);
            setCurrentRoute(RouteNames.PLAYER_PROFILE);
          }}
        >
          <div className="flex flex-col">
            <div className="flex items-center gap-2 xs:gap-4">
              <div className="scale-75 xs:scale-100">
                <CountryFlag countryCode={playerCountry} size="md" />
              </div>
              <span className="text-pk-gray-5 translate-y-[-5px] text-xl xs:text-2xl text-shadow font-bold  tracking-wide drop-shadow-lg">
                {username || "PLAYER"}
              </span>
            </div>
          </div>
        </div>

        {/* Currency & Limits */}
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentRoute(RouteNames.SEND_POKECOINS)}
              className="flex items-center gap-2 bg-pk-gray-1/90 border-2 border-pk-yellow-3 rounded-lg px-2 py-1 shadow-md hover:bg-pk-gray-1 transition-colors active:scale-95 cursor-pointer"
            >
              <img
                src="/assets/app-ui/poke-coin.png"
                alt="Coins"
                className="w-auto h-6 xs:h-8"
              />
              <span className="text-pk-yellow-3 font-bold text-sm">
                {pokeCoins ?? "..."}
              </span>
            </button>
            <button
              onClick={() => setCurrentRoute(RouteNames.SEND_POKESHARDS)}
              className="flex items-center gap-2 bg-pk-blue-1 border-2 border-pk-blue-5 rounded-lg px-2 py-1 shadow-md hover:bg-pk-blue-2 transition-colors active:scale-95 cursor-pointer"
            >
              <img
                src="/assets/app-ui/poke-shard.png"
                alt="Shards"
                className="w-auto h-6 xs:h-8"
              />
              <span className="text-pk-blue-5 font-bold text-sm">
                {pokeShards ?? "..."}
              </span>
            </button>
          </div>

          <button
            onClick={() => setIsRewardsDialogOpen(true)}
            className="flex items-center gap-2 bg-pk-gray-1/80 border border-pk-gray-3 rounded-md px-2 py-0.5 shadow-sm backdrop-blur-sm hover:bg-pk-gray-1/90 transition-colors cursor-pointer active:scale-95"
          >
            <span className="text-[10px] xs:text-xs font-bold text-pk-gray-4 uppercase tracking-wider">
              Rewards Left:
            </span>
            <span
              className={`font-bold text-xs xs:text-sm ${
                matchesRemaining > 0 ? "text-pk-green-5" : "text-pk-red-5"
              }`}
            >
              {matchesRemaining}/6
            </span>
          </button>
        </div>
      </div>

      {/* Ideas Board Banner - Alpha Feature */}
      {!currentRoute && (
        <div className="w-full flex justify-start">
          <button
            onClick={() => setCurrentRoute(RouteNames.IDEA_BOARD)}
            className={`flex items-center gap-2 rounded-lg px-2 py-1 shadow-md transition-all active:scale-[0.98] border-2 ${
              rewardAvailable
                ? "bg-pk-gold-3/90 border-pk-gold-5 animate-pulse-gold"
                : "bg-pk-purple-3/80 border-pk-purple-4 hover:bg-pk-purple-4/90"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <LightbulbIcon
                className={`w-4 h-4 ${
                  rewardAvailable ? "text-pk-gold-1" : "text-pk-yellow-3"
                }`}
                fill={rewardAvailable ? "currentColor" : "none"}
              />
              <span
                className={`font-bold text-[10px] xs:text-xs ${
                  rewardAvailable ? "text-pk-gold-1" : "text-pk-gray-1"
                }`}
              >
                Idea Board
              </span>
            </div>
            {showRewardEnticement ? (
              <div
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${
                  rewardAvailable
                    ? "bg-pk-gold-5/40 border-pk-gold-1/20"
                    : "bg-pk-purple-1/30 border-pk-purple-1/20"
                }`}
              >
                <img
                  src="/assets/app-ui/poke-coin.png"
                  className={`w-3 h-3 ${!rewardAvailable ? "opacity-70" : ""}`}
                  alt="Reward"
                />
                <span
                  className={`text-[9px] font-black uppercase ${
                    rewardAvailable ? "text-pk-gold-1" : "text-pk-purple-1"
                  }`}
                >
                  {rewardAvailable ? "Claim!" : `${votesToday}/3`}
                </span>
              </div>
            ) : (
              <span className="text-pk-purple-1 text-[9px] font-semibold opacity-80">
                Vote & earn →
              </span>
            )}
          </button>
        </div>
      )}

      <RewardsInfoDialog
        isOpen={isRewardsDialogOpen}
        onClose={() => setIsRewardsDialogOpen(false)}
      />
    </header>
  );
};
